import React, { useMemo, useRef, useEffect, useCallback, useState } from 'react'
import Card from 'antd/lib/card/Card'
import { CommonFilters, ReplayRecord, UserAvatar } from '@components'
import { t3cCallInRecordFilterItems } from 'src/components/CommonFilters/configs'
import { TableIdEnum } from '@configs/table'
import { getT3CCallInRecordList, getT3CCallInRecordCallbackPhone } from '@services/customer'
import { Table, Space, Tooltip, Button, message } from 'antd'
import { TableColumnProps } from '@hooks'
import { getStaffName, parseDuration, sStorage, checkT3CCallReady, showT3CCheckMessage, startT3CCall } from '@library'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { useTableService } from '@hooks/useTableService'
import { T3CCallInRecordFilter, T3CCallInRecordListItem, needTotalEnum, sortTypeEnum, T3CCallEndReasonEnum } from '@types'
import { FormValues } from '@tencent/meta-form'
import moment from 'moment'
import { cloneDeep } from 'lodash'
import { T3CCallEndReasonMap } from '@configs/map'
import { t3cStore } from '@store/t3c-store'
import { sessionStore } from '@store/session-store'
import { useShallow } from 'zustand/react/shallow'
import { ModuleNameMapping } from '@configs/constants'

// 话单生成中的特殊值
const GENERATING_CALLDATA = -1

// 扩展的呼入记录数据类型
interface CallInRecordData extends T3CCallInRecordListItem {
  durationText?: string
  failReasonText?: string
  skillGroupNamesText?: string
  disabled?: boolean
  playTips?: string
}

// 处理呼入记录数据
const handleCallInRecord = (list: T3CCallInRecordListItem[]): CallInRecordData[] => list?.map((item) => {
  const callRecordData: CallInRecordData = { ...item }
  const isGenerating = item?.duration === GENERATING_CALLDATA

  // 通话时间为-1时，通话时长展示为空
  callRecordData.durationText = isGenerating ? '话单生成中' : parseDuration(item?.duration, 's')
  callRecordData.failReasonText = T3CCallEndReasonMap[item?.failReason] || ''
  callRecordData.skillGroupNamesText = item?.skillGroupNames?.join('、') || '-'

  // 设置播放按钮状态
  if (isGenerating) {
    callRecordData.playTips = '话单生成中，请稍后再试'
    callRecordData.disabled = true
  } else if (item?.duration === 0) {
    callRecordData.playTips = '未接通，无录音'
    callRecordData.disabled = true
  } else {
    callRecordData.playTips = '收听录音'
    callRecordData.disabled = false
  }

  return callRecordData
})

const initFilter: T3CCallInRecordFilter = {
  page: 1,
  pageSize: 10,
  needTotal: needTotalEnum.ALL,
  sort: 'callBeginTime',
  sortType: sortTypeEnum.DESC,
}

// 缓存key
const FILTER_KEY = `${TableIdEnum.T3C_CALL_IN_RECORD}_FILTER`
const FILTER_VALUES_KEY = `${TableIdEnum.T3C_CALL_IN_RECORD}_FILTER_VALUES`

export default function T3CCallInRecord () {
  const {
    loading,
    list,
    pagination,
    filter,
    setFilter,
    resetFilter,
    rowKey,
    reload,
  } = useTableService(getT3CCallInRecordList, initFilter, '获取T3C呼入记录失败')

  const t3cCallInRecordList = useMemo<CallInRecordData[]>(() => handleCallInRecord(list), [list])

  const filterRef = useRef(null)

  // T3C 相关状态
  const { t3cReady, t3cErrorMsg, t3cfailed, reinitT3cSdk, curTabUUID, t3cSdkReady, t3cSdkReadyTime, t3cSdkUrl } = t3cStore(
    useShallow(s => ({
      t3cReady: s.t3cReady,
      t3cErrorMsg: s.t3cErrorMsg,
      t3cfailed: s.t3cfailed,
      reinitT3cSdk: s.reinitT3cSdk,
      curTabUUID: s.curTabUUID,
      t3cSdkReady: s.t3cSdkReady,
      t3cSdkReadyTime: s.t3cSdkReadyTime,
      t3cSdkUrl: s.t3cSdkUrl,
    }))
  )
  const userInfo = sessionStore(s => s.userInfo)
  const [isCallbacking, setIsCallbacking] = useState(false)

  // 回拨功能
  const handleCallback = useCallback(async (recordId: number) => {
    if (isCallbacking) {
      return
    }
    setIsCallbacking(true)
    setTimeout(() => {
      setIsCallbacking(false)
    }, 1500)

    // T3C 状态检查
    const checkResult = checkT3CCallReady({
      t3cReady,
      t3cSdkReady,
      t3cfailed,
      t3cErrorMsg,
      curTabUUID,
      t3cSdkReadyTime,
      t3cSdkUrl,
      userEmail: userInfo.email,
      useT3C: userInfo.useT3C,
      reinitT3cSdk,
    })

    if (!showT3CCheckMessage(checkResult)) {
      return
    }

    // 获取真实电话号码
    const [phoneRes, phoneErr] = await getT3CCallInRecordCallbackPhone({ recordId })
    if (phoneErr) {
      message.error(`获取回拨电话号码失败: ${phoneErr.message}`)
      return
    }

    // 埋点上报
    window.XSY_TRACK?.trackClick('tccc_call', {
      content: {
        belong_module: userInfo?.belongModule,
        belong_module_name: ModuleNameMapping[userInfo?.belongModule],
        follower: getStaffName(userInfo.salesName, userInfo.salesNameCh),
        tccc_call_tabUUID: window?.tccc?.uuid,
        tccc_call_recordId: recordId,
        tccc_call_phone: phoneRes?.phone,
        tccc_call_from: 'callIn_call',
      },
    })

    // 执行外呼
    const debugInfo = `tabUUID: ${curTabUUID}; userId: ${userInfo?.email};当前时间: ${new Date().toLocaleString()}`
    const success = await startT3CCall({
      phoneNumber: phoneRes?.phone,
      uui: JSON.stringify({ recordId: recordId?.toString() }),
    }, debugInfo)

    if (success) {
      message.success('回拨成功')
    }
  }, [isCallbacking, t3cReady, t3cErrorMsg, t3cfailed, reinitT3cSdk, curTabUUID, t3cSdkReady, t3cSdkReadyTime, t3cSdkUrl, userInfo])

  const columns: TableColumnProps<CallInRecordData>[] = useMemo(() => [
    {
      title: 'recordId',
      dataIndex: 'recordId',
      width: 100,
    },
    { title: '跟进人', dataIndex: 'salesName', width: 260, render: value => <UserAvatar salesName={value} /> },
    {
      title: '呼叫时间',
      dataIndex: 'callBeginTime',
      width: 180,
    },
    {
      title: '备注',
      dataIndex: 'remark',
      width: 200,
      render: (val) => val || '-',
    },
    {
      title: '主叫号码',
      dataIndex: 'caller',
      width: 180,
    },
    {
      title: '被叫号码',
      dataIndex: 'phone',
      width: 180,
    },
    {
      title: '接入状态',
      dataIndex: 'failReasonText',
      width: 180,
    },
    {
      title: (<>通话时长<Tooltip title="指从通话接通到通话结束的时间"><QuestionCircleOutlined /></Tooltip></>),
      _title: '通话时长',
      dataIndex: 'durationText',
      width: 120,
    },
    {
      title: '技能组',
      dataIndex: 'skillGroupNamesText',
      width: 200,
    },
    {
      title: 'sessionId',
      dataIndex: 'sessionId',
      width: 200,
    },
    {
      title: '操作',
      dataIndex: 'actions',
      width: 180,
      render: (_, item) => {
        return (
          <Space>
            <Button
              type="link"
              style={{ padding: 0 }}
              loading={isCallbacking}
              onClick={() => handleCallback(item.recordId)}
            >
              回拨
            </Button>
            <ReplayRecord
              disabled={item.disabled}
              playTips={item.playTips}
              logId={`${item.recordId}`}
              title={'呼入通话录音'}
              key={item?.recordId}
              isCallIn={true}
              btnText="播放录音"
            />
          </Space>
        )
      },
    },
  ], [])

  // 分页变化时缓存
  useEffect(() => {
    const { current: page, pageSize } = pagination
    const val: FormValues = sStorage.get(FILTER_KEY)
    sStorage.set(FILTER_KEY, { ...val, page, pageSize: 1, _pageSize: pageSize })
  }, [pagination])

  // 从缓存恢复筛选条件
  useEffect(() => {
    const val: FormValues = sStorage.get(FILTER_VALUES_KEY)
    if (filterRef?.current && val) {
      const vals = cloneDeep(val)
      // 时间
      if (val.createTimeFrom || val.createTimeTo) {
        const start = val.createTimeFrom ? moment(val.createTimeFrom) : undefined
        const end = val.createTimeTo ? moment(val.createTimeTo) : undefined
        vals.createTime = [start, end]
      }
      filterRef.current.setValues(vals)
      setFilter(val)
    }
  }, [filterRef])

  /** 搜索 */
  const handleSearch = (filter) => {
    const val = { ...filter, page: 1 }
    const newVal = Object.fromEntries(
      Object.entries({ ...val }).filter((item: [string, any]) => {
        const [, value] = item
        if (value === null || value === undefined) return false
        if (typeof value === 'object') {
          return Object.keys(value).length !== 0
        }
        return value !== ''
      }),
    )
    sStorage.set(FILTER_KEY, newVal)
    setFilter(val)
    reload()
  }

  return (
    <>
      <Card className="mt20 mb20">
        <CommonFilters
          cRef={filterRef}
          className="custom-Label"
          itemList={t3cCallInRecordFilterItems}
          onSubmit={handleSearch}
          onReset={() => {
            sStorage.remove(FILTER_KEY)
            resetFilter(initFilter)
          }}
          tableId={TableIdEnum.T3C_CALL_IN_RECORD}
          standstill={10}
        />
      </Card>
      <Card>
        <Table
          className="mt20"
          loading={loading}
          pagination={pagination}
          rowKey={rowKey}
          dataSource={t3cCallInRecordList}
          columns={columns}
        />
      </Card>
    </>
  )
}
